import { Check, ChevronRight } from 'lucide-react';
import {
  SubmissionStatus,
  statusRank,
  type SubmissionStatus as SubmissionStatusCode,
} from '@clawhire/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const MAIN_STEPS: SubmissionStatusCode[] = [
  'SCREENING',
  'SUBMITTED',
  'HR_INTERVIEW',
  'CLIENT_INTERVIEW',
  'OFFER',
  'PLACED',
];
const SIDE_OUTCOMES: SubmissionStatusCode[] = ['REJECTED', 'WITHDRAWN'];

export interface StatusStepperProps {
  status: SubmissionStatusCode;
  /** The user picked a status (never the current one). */
  onSelect: (status: SubmissionStatusCode) => void;
  disabled?: boolean;
  busy?: boolean;
  className?: string;
}

/**
 * Screening → Submitted → HR interview → Client interview → Offer → Placed, with Rejected and
 * Withdrawn as side outcomes. Any step can be chosen (the API allows every transition; going
 * back is a deliberate correction).
 */
export function StatusStepper({
  status,
  onSelect,
  disabled = false,
  busy = false,
  className,
}: StatusStepperProps) {
  const sideActive = SIDE_OUTCOMES.includes(status);
  const currentRank = statusRank(status);

  return (
    <nav
      aria-label="Submission status"
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-2', className)}
    >
      <ol className="flex flex-wrap items-center gap-1">
        {MAIN_STEPS.map((code, index) => {
          const active = code === status;
          const done = !sideActive && !active && statusRank(code) < currentRank;
          return (
            <li key={code} className="flex items-center gap-1">
              <button
                type="button"
                aria-current={active ? 'step' : undefined}
                disabled={disabled || busy || active}
                onClick={() => onSelect(code)}
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : done
                      ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-transparent bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  sideActive && 'opacity-70',
                )}
              >
                <span
                  className={cn(
                    'flex h-[1.125rem] w-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full text-[10px] tabular-nums',
                    active
                      ? 'bg-primary-foreground/20'
                      : done
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background/70',
                  )}
                  aria-hidden="true"
                >
                  {done ? <Check className="h-3 w-3" /> : index + 1}
                </span>
                {SubmissionStatus.label(code)}
              </button>
              {index < MAIN_STEPS.length - 1 ? (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              ) : null}
            </li>
          );
        })}
      </ol>
      <div className="flex items-center gap-1" role="group" aria-label="Outcomes">
        {SIDE_OUTCOMES.map((code) => {
          const active = code === status;
          return (
            <Button
              key={code}
              type="button"
              size="sm"
              variant={active ? 'destructive' : 'outline'}
              aria-current={active ? 'step' : undefined}
              disabled={disabled || busy || active}
              onClick={() => onSelect(code)}
              className="h-8 rounded-full"
            >
              {SubmissionStatus.label(code)}
            </Button>
          );
        })}
      </div>
    </nav>
  );
}
